import React from "react";
import {
  GitBranch,
  MessageSquare,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import { Card } from "@shared/ui/card";
import {
  WorkflowPerformance,
  WorkflowPerformanceSectionProps,
  WorkflowStatsResponse,
} from "../../types";
import { isWorkPerformance } from "../../utils";
import { SectionWrapper } from "../shared/sectionWrapper";
import { MetricCard } from "../shared/metricCard";
import { ProgressItem } from "../shared/progressItem";

export const Workflow: React.FC<WorkflowPerformanceSectionProps> = ({
  data,
  statData,
}) => {
  const isValidData = data && isWorkPerformance(data);
  const hasActiveWorkflows = isValidData && data.activeWorkflowsCount > 0;

  // Define metric cards configuration
  const metricCardsConfig = [
    {
      title: "Active Workflows",
      value: data?.activeWorkflowsCount || 0,
      subtitle: ``,
      icon: GitBranch,
      variant: "primary" as const,
    },
    {
      title: "Total Messages",
      value: data?.totalMessagesCount || 0,
      icon: MessageSquare,
      variant: "info" as const,
    },
    {
      title: "Avg Success Rate",
      value: `${(data?.avgSuccessRate || 0).toFixed(1)}%`,
      icon: TrendingUp,
      variant: "success" as const,
    },
    {
      title: "Total Failures",
      value: data?.totalFailuresCount || 0,
      icon: AlertTriangle,
      variant: "warning" as const,
    },
  ];

  // Define chart configurations
  const chartsConfig = [
    {
      title: "Messages Distribution",
      keyPrefix: "msg",
      valueKey: "successCount" as const,
      showValue: true,
      showPercentage: true,
      variant: undefined,
    },
    {
      title: "Success Rate Distribution",
      keyPrefix: "success",
      valueKey: "successRate" as const,
      showValue: false,
      showPercentage: true,
      variant: "success" as const,
    },
  ];

  return (
    <SectionWrapper
      title="Workflow Performance"
      icon={GitBranch}
      isEmpty={!hasActiveWorkflows}
      emptyStateProps={{
        icon: GitBranch,
        title: "No active workflows",
        description: "Create your first workflow to see performance metrics",
      }}
      loadingType="stats"
      loadingCount={4}
    >
      {hasActiveWorkflows && (
        <>
          {/* Stats Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {metricCardsConfig.map((config, index) => (
              <MetricCard
                key={`metric-${index}`}
                title={config.title}
                value={config.value}
                subtitle={config.subtitle}
                icon={config.icon}
                position="right"
                variant={config.variant}
              />
            ))}
          </div>

          {/* Performance Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {chartsConfig.map((chart, chartIndex) => (
              <Card key={`chart-${chartIndex}`} className="p-4">
                <h4 className="text-sm font-medium mb-4">{chart.title}</h4>
                <div className="space-y-4">
                  {statData?.results?.map((item, itemIndex) => (
                    <ProgressItem
                      key={`${chart.keyPrefix}-${itemIndex}`}
                      label={item.workflowName}
                      value={item[chart.valueKey]}
                      percentage={
                        chart.valueKey === "successRate"
                          ? item.successRate
                          : item.percentage
                      }
                      showValue={chart.showValue}
                      showPercentage={chart.showPercentage}
                      variant={chart.variant}
                    />
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </SectionWrapper>
  );
};
